import { formatNumber } from "pages/MatchDetail/MatchedOdds";
import React, { useState } from "react";
import { Table } from "react-bootstrap";
import "bootstrap-icons/font/bootstrap-icons.css";
import "./style.css";

interface FancyPLData {
  sport_name: string;
  uplineProfitLoss: number;
  total: number;
  commission: number;
  type: number;
  sport_id: string;
  total_net_pl: string;
  series_name: string;
  series_id: string;
  event_id: string;
  match_name: string;
  event_name: string;
}

interface FancyPLTableProps {
  fancyPLData: FancyPLData[];
  total: number;
  onClickRow?: (event_id: string, sport_id: string, type: string) => void;
}

const EMarketReportTable: React.FC<FancyPLTableProps> = ({ fancyPLData, total, onClickRow }) => {
  const [sortKey, setSortKey] = useState<keyof FancyPLData | "">("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");

  const handleSort = (key: keyof FancyPLData) => {
    if (sortKey === key) {
      setSortOrder(prev => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortOrder("asc");
    }
  };

  const getSortClass = (key: keyof FancyPLData) => {
    const isActive = sortKey === key;
    const upClass = isActive && sortOrder === "asc" ? "text-dark" : "text-muted";
    const downClass = isActive && sortOrder === "desc" ? "text-dark" : "text-muted";

    return (
      <span className="d-flex flex-column ms-2">
        <i className={`bi bi-caret-up-fill ${upClass}`} style={{ lineHeight: 0.6 }} />
        <i className={`bi bi-caret-down-fill ${downClass}`} style={{ lineHeight: 0.6 }} />
      </span>
    );
  };

  const sortedData = sortKey
    ? [...fancyPLData].sort((a, b) => {
        const aVal = a[sortKey];
        const bVal = b[sortKey];

        if (typeof aVal === "string" && typeof bVal === "string") {
          return sortOrder === "asc"
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        }

        const aNum = typeof aVal === "number" ? aVal : parseFloat(aVal as string);
        const bNum = typeof bVal === "number" ? bVal : parseFloat(bVal as string);

        if (isNaN(aNum) || isNaN(bNum)) return 0;
        return sortOrder === "asc" ? aNum - bNum : bNum - aNum;
      })
    : fancyPLData;

  return (
    <div className="table-responsive">
      <Table striped hover responsive className="table-centered align-middle">
        <thead className="table-light">
          <tr>
            <th onClick={() => handleSort("sport_name")} style={{ cursor: "pointer" }}>
              <div className="d-flex justify-content-between align-items-center">
                <span>Sport Name</span>
                {getSortClass("sport_name")}
              </div>
            </th>
            <th onClick={() => handleSort("series_name")} style={{ cursor: "pointer" }}>
              <div className="d-flex justify-content-between align-items-center">
                <span>Event Name</span>
                {getSortClass("series_name")}
              </div>
            </th>
            <th onClick={() => handleSort("match_name")} style={{ cursor: "pointer" }}>
              <div className="d-flex justify-content-between align-items-center">
                <span>Market Name</span>
                {getSortClass("match_name")}
              </div>
            </th>
            <th onClick={() => handleSort("total_net_pl")} style={{ cursor: "pointer" }}>
              <div className="d-flex justify-content-between align-items-center">
                <span>Total Net PL</span>
                {getSortClass("total_net_pl")}
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedData.map((item, index) => (
            <tr
              key={index}
              onClick={() => onClickRow?.(item.event_id, item.sport_id, String(item.type))}
              style={{ cursor: "pointer" }}
            >
              <td>{item.sport_name}</td>
              <td>{item.series_name}</td>
              <td>{item.match_name}</td>
              <td className={Number(item.total_net_pl) < 0 ? "text-danger" : "text-success"}>
                {formatNumber(item.total_net_pl)}
              </td>
            </tr>
          ))}
          <tr>
            <td><strong>Total</strong></td>
            <td></td>
            <td></td>
            <td className={Number(total) < 0 ? "text-danger" : "text-success"}>
              <strong>{formatNumber(String(total))}</strong>
            </td>
          </tr>
        </tbody>
      </Table>
    </div>
  );
};

export default EMarketReportTable;
